import { z } from "zod";

/**
 * Centralized file type configuration to maintain consistency across the application.
 * This eliminates duplication of file type logic in multiple components.
 */

// Supported video file extensions
export const VIDEO_FILE_EXTENSIONS = ["mp4", "mov"] as const;

// 2 GB binary (2 * 1024³ = 2,147,483,648 bytes). macOS Finder and most OS file
// managers display sizes in *decimal* GB (10⁹ bytes), so a file labeled "2.15 GB"
// in Finder is ~2.003 binary GB here.
export const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024 * 1024;

// Per-file limit for the AI control-build upload flow (CreateControlPageV2).
// Large documents (e.g. multi-hundred-MB Excel workbooks) overwhelm downstream
// document processing and surface to the user as a generic "Session Failed",
// so we reject them client-side before upload rather than after a long,
// silent failure. This limit matches the presigned-upload threshold
// (PRESIGNED_UPLOAD_THRESHOLD_BYTES in src/api/presignedUpload.ts), so files
// admitted here always take the direct upload path.
export const CONTROL_BUILD_MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

// Supported text file extensions
export const TEXT_FILE_EXTENSIONS = [
  "txt",
  "text",
  "log",
  "md",
  "markdown",
  "json",
  "xml",
  "html",
  "css",
  "js",
  "ts",
  "tsx",
  "jsx",
  "py",
  "java",
  "c",
  "cpp",
  "h",
  "sql",
  "yml",
  "yaml",
  "go",
];

// Extensions that should be accepted for upload/processing.
// The backend counterpart is DOCUMENT_UPLOAD_FILE_TYPES in
// soxhub/midship-turbo-broccoli `midship/heretic/zip_handler.py`, which gates
// the same uploads server-side (and the files extracted from uploaded ZIPs).
// The two lists are not identical — text extensions below all resolve to "txt"
// server-side — but a document format added here needs adding there too, or
// the upload is rejected after the user has already selected the file.
export const ACCEPTED_FILE_EXTENSIONS = [
  // Documents
  "msg",
  // Emails exported from non-Outlook clients (and from some Outlook views).
  // Like .msg, an .eml is converted server-side to a body PDF and its
  // attachments become first-class child documents, so the fileType seen here
  // is "eml" only while the document is still processing.
  "eml",
  "pdf",
  "docx",
  // Legacy binary Word (Word 97). Converted server-side to PDF at
  // preprocess — like .ppt — so the fileType seen here is "doc" only while
  // the document is still processing (or when conversion degrades). Without
  // this arm, one .doc document fails the whole-collection zod parse and
  // takes down the entire documents fetch for its control.
  "doc",
  "pptx",
  "ppt",
  // Images
  "jpg",
  "jpeg",
  "png",
  "webp",
  // Spreadsheets
  "csv",
  "xlsx",
  "xlsm",
  "xls",
  "xlsb",
  // Video
  ...VIDEO_FILE_EXTENSIONS,
  // Text files
  ...TEXT_FILE_EXTENSIONS,
] as const;

// The formats a document may be flagged with as an input to an analytics
// workflow run. A workflow file parameter is handed to a run that reads *rows*
// out of the file, so a recording, a scan or a slide deck cannot answer one;
// neither can a format the engine ships no reader for, whether it holds rows or
// not.
//
// Narrower than ACCEPTED_FILE_EXTENSIONS above, and deliberately so: what may be
// uploaded and what a run can consume are different questions, and this is the
// second. `xlsb` is on that list and not on this one for the second reason, and
// it is the one absence that reads as an oversight: it holds rows, but Cascade
// ships no reader for the binary container. Cascade has no pyxlsb, which
// `pandas.read_excel` requires for that format, and its Excel ingest calls
// `openpyxl.load_workbook` first while swallowing only openpyxl's `.xls`
// message — so openpyxl's separate refusal of `.xlsb` escapes unclassified and
// the run ends as a generic technical error naming nothing the auditor can act
// on.
//
// The backend counterpart is ANALYTICS_INPUT_FILE_TYPES in soxhub/
// midship-turbo-broccoli `midship/heretic/analytics_input/formats.py`, and that
// is the enforcement point — the write route refuses a format outside it however
// the request was composed. This list only decides what the UI offers, so the
// two must name the same formats or the surface offers what the route refuses.
export const ANALYTICS_INPUT_FILE_EXTENSIONS = [
  "csv",
  "xls",
  "xlsm",
  "xlsx",
] as const;

// File type enum for Zod validation (derived from ACCEPTED_FILE_EXTENSIONS)
export const FileType = z.enum(ACCEPTED_FILE_EXTENSIONS);

export type FileType = z.infer<typeof FileType>;

/**
 * Generates an accept string for file input elements using specific extensions.
 * This is more precise than using MIME types like "text/*" which would accept all text files.
 *
 * @returns A comma-separated string of file extensions for the accept attribute
 * @example ".pdf,.jpg,.jpeg,.png,.webp,.csv,.xlsx,.xls,.txt,.json,.py,..."
 */
export function getFileInputAcceptString(): string {
  return ACCEPTED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(",");
}

/**
 * Checks if a file extension is a text file type.
 *
 * @param extension - The file extension to check (without the dot)
 * @returns True if the extension is a supported text file type
 */
export function isTextFileExtension(extension: string): boolean {
  return TEXT_FILE_EXTENSIONS.includes(extension.toLowerCase());
}

/**
 * The literal tuple stays the exported shape because a caller renders it as a
 * list; this set is what an arbitrary string is checked against, so the lookup
 * needs no widening of that tuple.
 */
const ANALYTICS_INPUT_EXTENSION_SET: ReadonlySet<string> = new Set(
  ANALYTICS_INPUT_FILE_EXTENSIONS,
);

/**
 * Whether a document of this format can be flagged as an analytics input.
 *
 * An unknown format reads as not flaggable. The control this gates is an
 * affordance, so the safe default is to withhold it and let the auditor find
 * out from a route that can actually see what was stored, rather than to offer
 * a write that will be refused.
 *
 * @param extension - The document's file type, without the dot
 */
export function isAnalyticsInputFileExtension(
  extension: string | null | undefined,
): boolean {
  if (!extension) return false;
  return ANALYTICS_INPUT_EXTENSION_SET.has(extension.toLowerCase());
}

/**
 * Checks if a file extension is a video file type.
 *
 * @param extension - The file extension to check (without the dot)
 * @returns True if the extension is a supported video file type
 */
export function isVideoFileExtension(extension: string): boolean {
  return VIDEO_FILE_EXTENSIONS.includes(
    extension.toLowerCase() as (typeof VIDEO_FILE_EXTENSIONS)[number],
  );
}

/**
 * Gets the file extension from a filename.
 *
 * @param filename - The filename to extract extension from
 * @returns The extension without the dot, or empty string if none
 */
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot === -1) return "";
  return filename.slice(lastDot + 1).toLowerCase();
}

/**
 * Formats bytes to a human-readable string (e.g., "50 MB", "1 GB").
 *
 * @param bytes - The size in bytes
 * @returns Human-readable size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(0)} GB`;
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${bytes} bytes`;
}

export type FileSizeValidation =
  | { valid: true }
  | { valid: false; error: string };

export function validateFileSize(
  file: File,
  maxBytes: number = MAX_FILE_SIZE_BYTES,
): FileSizeValidation {
  if (file.size === 0) {
    return {
      valid: false,
      error: `${file.name} is empty (0 bytes). Please upload a file with content.`,
    };
  }
  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `${file.name} exceeds the ${formatFileSize(maxBytes)} upload limit.`,
    };
  }
  return { valid: true };
}

// Mirrors the backend contract in soxhub/midship-turbo-broccoli `midship/util/pdf.py`
// (is_pdf_content): a PDF contains the "%PDF-" header within its first 1024 bytes. The
// spec permits arbitrary bytes before it, so we search rather than match at offset 0.
// Keep these in sync with the backend — if they drift, the FE will start passing files
// the backend rejects (reintroducing the late server-side failure this guard prevents).
const PDF_HEADER = "%PDF-";
const PDF_HEADER_SEARCH_WINDOW = 1024;

export type FileContentValidation =
  | { valid: true }
  | { valid: false; error: string };

/**
 * Validates that a file claiming to be a PDF actually contains PDF data.
 *
 * Catches files saved or renamed with a `.pdf` extension that are really something
 * else (an HTML page, a Word or Excel document). These pass the extension check but
 * fail to parse server-side, so we reject them before upload. Only `.pdf` files are
 * inspected; every other type passes through unchanged.
 *
 * @param file - The file to inspect
 * @returns Valid unless the file is named `.pdf` but lacks the PDF header
 */
export async function validatePdfContent(
  file: File,
): Promise<FileContentValidation> {
  if (getFileExtension(file.name) !== "pdf") {
    return { valid: true };
  }
  let headerBytes: ArrayBuffer;
  try {
    headerBytes = await file.slice(0, PDF_HEADER_SEARCH_WINDOW).arrayBuffer();
  } catch {
    // The file became unreadable between selection and read (e.g. an unhydrated
    // cloud placeholder or an ejected drive). Fail open — this is a fast-fail UX
    // guard, and the upload path and backend will surface a genuine read error.
    return { valid: true };
  }
  const headerText = new TextDecoder("latin1").decode(headerBytes);
  if (!headerText.includes(PDF_HEADER)) {
    return {
      valid: false,
      error: `${file.name} is named as a PDF but doesn't contain PDF data — it looks like a different file type. Please upload a valid PDF.`,
    };
  }
  return { valid: true };
}
